import { inject, Injectable } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { nameOf } from '../../../../../../../core/functions/string-helpers';
import { FormGroupType } from '../../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../../core/models/regexp-pattern/regex-pattern';
import { CardOnFileFormConstants } from '../../shared/constants/card-on-file-form-constants';
import { CardOnFileFormService } from '../../shared/services/card-on-file-form.service';
import { CreateCardOnFileValidationMessages } from '../constants/create-card-on-file-validation-messages';
import { passesLuhnCheck } from '../helpers/credit-card-luhn';
import { CreateCardOnFileFormModel } from '../models/create-card-on-file-form-model';

@Injectable({
    providedIn: 'root',
})
export class CreateCardOnFileFormService {
    private readonly cardOnFileFormService = inject(CardOnFileFormService);

    /**
     * Generates a form group definition for creating a card on file, with validation rules applied to each form control.
     *
     * @param isFromOrderContext A boolean indicating whether the form is being used in the context of an order, which determines the default value of the store for future use control.
     * @return A form group definition for the create card on file form, with appropriate validators for each field.
     */
    getFormDefinition(
        isFromOrderContext: boolean,
    ): FormGroup<FormGroupType<CreateCardOnFileFormModel>> {
        const form = new FormGroup<FormGroupType<CreateCardOnFileFormModel>>({
            firstName: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(
                    CardOnFileFormConstants.FIRST_NAME_MAX_LENGTH,
                ),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            lastName: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(
                    CardOnFileFormConstants.LAST_NAME_MAX_LENGTH,
                ),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            company: new FormControl<string | null>(null, [
                Validators.maxLength(
                    CardOnFileFormConstants.COMPANY_MAX_LENGTH,
                ),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            address: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(
                    CardOnFileFormConstants.ADDRESS_MAX_LENGTH,
                ),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            city: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(CardOnFileFormConstants.CITY_MAX_LENGTH),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            state: new FormControl<string | null>(null, [Validators.required]),
            zip: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(CardOnFileFormConstants.ZIP_MAX_LENGTH),
                Validators.pattern(RegExPattern.PostalCode),
            ]),
            cardNumber: new FormControl<string | null>(null, [
                Validators.required,
                this.cardNumberValidator(),
            ]),
            expirationMonth: new FormControl<string | null>(null, [
                Validators.required,
                this.cardOnFileFormService.expirationMonthValidator(),
            ]),
            expirationYear: new FormControl<string | null>(null, [
                Validators.required,
            ]),
            cardCode: new FormControl<string | null>(null, [
                Validators.required,
                Validators.pattern(RegExPattern.NumericOnly),
                this.cardCodeValidator(),
            ]),
            // If the card is being added in the context of an order, default to not storing for future use.
            // If it is not being added in the context of an order, default to storing for future use (null value
            // will be treated as true in this case since the option will be hidden).
            storeForFutureUse: new FormControl<boolean | null>(
                isFromOrderContext ? false : null,
            ),
            isDefault: new FormControl<boolean>(false),
        });

        return form;
    }

    /**
     * Verifies if the provided card number matches the format of a Visa card number, which starts with '4' and has a length of 13, 16, or 19 digits.
     *
     * @param cardNumber The card number to validate as a Visa card number.
     * @returns True if the card number is a valid Visa card number, false otherwise.
     */
    isVisaNumber(cardNumber: string): boolean {
        return (
            cardNumber.startsWith('4') &&
            (cardNumber.length === 13 ||
                cardNumber.length === 16 ||
                cardNumber.length === 19)
        );
    }

    /**
     * Checks if the provided card number matches the format of a Mastercard card number.
     * Mastercard numbers can start with '51' through '55' and have a length of 16 digits, or they can start with a number in the range of '2221' to '2720' and also have a length of 16 digits.
     *
     * @param cardNumber The card number to validate as a Mastercard card number.
     * @returns True if the card number is a valid Mastercard card number, false otherwise.
     */
    isMastercardNumber(cardNumber: string): boolean {
        if (cardNumber.length !== 16) return false;

        if (
            cardNumber.startsWith('5') &&
            cardNumber[1] >= '1' &&
            cardNumber[1] <= '5'
        ) {
            return true;
        }

        const prefix = parseInt(cardNumber.substring(0, 6), 10);
        return !isNaN(prefix) && prefix >= 222100 && prefix <= 272099;
    }

    /**
     * Determines if the provided card number matches the format of an American Express card number, which starts with '34' or '37' and has a length of 15 digits.
     *
     * @param cardNumber The card number to validate as an American Express card number.
     * @returns True if the card number is a valid American Express card number, false otherwise.
     */
    isAmericanExpressNumber(cardNumber: string): boolean {
        return (
            cardNumber.length === 15 &&
            (cardNumber.startsWith('34') || cardNumber.startsWith('37'))
        );
    }

    /**
     * Validates the card number for a Visa, Mastercard, or American Express format, then Luhn checksum.
     */
    private cardNumberValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const cardNumber = control.value as string | null;
            if (!cardNumber) {
                return null;
            }

            const isValidType =
                this.isVisaNumber(cardNumber) ||
                this.isMastercardNumber(cardNumber) ||
                this.isAmericanExpressNumber(cardNumber);

            if (!isValidType) {
                return {
                    [nameOf<typeof CreateCardOnFileValidationMessages>(
                        'cardNumberNotAValidType',
                    )]:
                        CreateCardOnFileValidationMessages.cardNumberNotAValidType,
                };
            }

            if (!passesLuhnCheck(cardNumber)) {
                return {
                    [nameOf<typeof CreateCardOnFileValidationMessages>(
                        'cardNumberInvalidLuhn',
                    )]:
                        CreateCardOnFileValidationMessages.cardNumberInvalidLuhn,
                };
            }

            return null;
        };
    }

    /**
     * Validates the card code based on the card type determined from the card number.
     * Visa and Mastercard require a 3-digit code, while American Express requires a 4-digit code.
     */
    private cardCodeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const cardCode = control.value as string | null;
            if (!cardCode) {
                return null;
            }

            const form = control.parent as FormGroup<
                FormGroupType<CreateCardOnFileFormModel>
            >;
            const cardNumber = form.controls.cardNumber.value;

            if (!cardNumber) {
                return null;
            }

            if (
                this.isVisaNumber(cardNumber) ||
                this.isMastercardNumber(cardNumber)
            ) {
                if (
                    cardCode.length ===
                    CardOnFileFormConstants.CARD_CODE_MASTERCARD_VISA_LENGTH
                ) {
                    return null;
                }
            }

            if (this.isAmericanExpressNumber(cardNumber)) {
                if (
                    cardCode.length ===
                    CardOnFileFormConstants.CARD_CODE_AMEX_LENGTH
                ) {
                    return null;
                }
            }

            return {
                [nameOf<typeof CreateCardOnFileValidationMessages>(
                    'cardCodeInvalid',
                )]: CreateCardOnFileValidationMessages.cardCodeInvalid,
            };
        };
    }
}
